// src/mobile/components/PhoneFrame.tsx
/* Movatic WMS — PhoneFrame (Coletor) | CesarFilho 932048016 */
import type { ReactNode } from "react";

type PhoneFrameProps = {
    title?: string;
    subtitle?: string;
    right?: ReactNode;
    className?: string;
    children?: ReactNode;
};

export default function PhoneFrame({
    title,
    subtitle,
    right,
    className,
    children,
}: PhoneFrameProps) {
    return (
        <div className="mx-auto w-full max-w-[420px] px-3 sm:px-0 py-3">
            {/* Moldura externa com “metal”/relevo */}
            <div
                className={[
                    "relative mx-auto",
                    "rounded-[2rem] p-[6px]",
                    "bg-gradient-to-br from-neutral-200/80 via-neutral-300/70 to-neutral-400/50",
                    "dark:from-neutral-800/80 dark:via-neutral-700/70 dark:to-neutral-600/60",
                    "shadow-[0_12px_28px_rgba(0,0,0,.35),inset_0_1px_0_rgba(255,255,255,.28)]",
                ].join(" ")}
            >
                {/* Borda preta/bezel do aparelho */}
                <div
                    className={[
                        "rounded-[1.8rem] p-1",
                        "bg-neutral-950 dark:bg-black",
                        "shadow-[inset_0_2px_6px_rgba(255,255,255,.06)]",
                    ].join(" ")}
                >
                    {/* Tela (screen) */}
                    <div
                        className={[
                            "relative rounded-[1.4rem] overflow-hidden",
                            "bg-white dark:bg-neutral-950",
                            "ring-1 ring-neutral-200/80 dark:ring-neutral-800",
                        ].join(" ")}
                    >
                        {/* NOTCH + câmera/alto-falante (overlay visual) */}
                        <div
                            aria-hidden
                            className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-1 h-6 w-36 rounded-b-2xl
                         bg-neutral-950/95 dark:bg-black/80 ring-1 ring-neutral-700/40 flex items-start justify-center"
                        >
                            <div className="mt-1.5 h-1.5 w-16 rounded-full bg-neutral-700/70" />
                            <div className="absolute right-6 top-[7px] h-2.5 w-2.5 rounded-full bg-neutral-700/70" />
                        </div>

                        {/* “Vidro”/brilho sutil */}
                        <div
                            aria-hidden
                            className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-40 dark:opacity-20
                         bg-[linear-gradient(120deg,rgba(255,255,255,.18),rgba(255,255,255,0)_45%)]"
                        />

                        {/* Topbar da tela (opcional) */}
                        {(title || subtitle || right) && (
                            <div className="relative z-[1] flex items-center justify-between gap-3 border-b border-neutral-200 dark:border-neutral-800 px-3 pt-9 pb-3">
                                <div className="min-w-0">
                                    {title && <div className="font-medium truncate">{title}</div>}
                                    {subtitle && (
                                        <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                                            {subtitle}
                                        </div>
                                    )}
                                </div>
                                {right ? <div className="shrink-0">{right}</div> : null}
                            </div>
                        )}

                        {/* Conteúdo da tela */}
                        <div className={["relative z-[1] px-3 pb-3 pt-12", className ?? ""].join(" ").trim()}>
                            {children}
                        </div>
                    </div>
                </div>

                {/* Brilho suave na moldura metálica */}
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-[2rem]
                     bg-[radial-gradient(120%_40%_at_10%_0%,rgba(255,255,255,.25),rgba(255,255,255,0)_60%)]
                     dark:bg-[radial-gradient(120%_40%_at_10%_0%,rgba(255,255,255,.12),rgba(255,255,255,0)_60%)]"
                />
            </div>
        </div>
    );
}
